import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Brain, Sparkles, Send, History, Crown, AlertTriangle, TrendingUp, Clock, Shield, Target, DollarSign } from 'lucide-react';
import { toast } from 'sonner';


const FOCUS_AREAS = [
  { value: 'general', label: 'General Optimization', icon: Sparkles, description: 'Overall protocol review and suggestions' },
  { value: 'dosing', label: 'Dosing Strategy', icon: Target, description: 'Dose amounts and frequency optimization' },
  { value: 'timing', label: 'Timing & Schedule', icon: Clock, description: 'When to take peptides for best results' },
  { value: 'safety', label: 'Safety Review', icon: Shield, description: 'Side effects and risk assessment' },
  { value: 'effectiveness', label: 'Effectiveness', icon: TrendingUp, description: 'Maximize research outcomes' },
  { value: 'cost', label: 'Cost Optimization', icon: DollarSign, description: 'Reduce cost without sacrificing results' }
];

export default function AIOptimization() {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [focusArea, setFocusArea] = useState('general');
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState(null);

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  useEffect(() => {
    if (isPremium) {
      fetchHistory();
    }
  }, [isPremium]);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/ai/optimization-history`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPremium) {
      toast.error('Premium subscription required');
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const res = await fetch(`${API}/ai/optimize-protocol`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          focus_area: focusArea,
          question: question || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        setResponse(data);
        fetchHistory();
        toast.success('Analysis complete');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to get AI recommendations');
    } finally {
      setLoading(false);
    }
  };

  if (!isPremium) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="ai-optimization-page">
        <div className="text-center py-12">
          <Brain className="h-20 w-20 mx-auto mb-6 text-primary opacity-50" />
          <h1 className="text-4xl font-bold mb-4">AI Protocol Optimization</h1>
          <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
            Get personalized recommendations powered by AI to optimize your research protocols.
          </p>
          <Card className="p-8 glass-card max-w-md mx-auto">
            <Crown className="h-12 w-12 mx-auto mb-4 text-primary" />
            <h2 className="text-xl font-semibold mb-2">Premium Feature</h2>
            <p className="text-muted-foreground mb-6">
              AI Protocol Optimization is available exclusively to Premium members.
            </p>
            <Link to="/subscription">
              <Button className="w-full">
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Premium
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="ai-optimization-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2 flex items-center gap-3">
            <Brain className="h-10 w-10 text-primary" />
            AI Optimization
          </h1>
          <p className="text-muted-foreground">Get personalized protocol recommendations</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => setShowHistory(!showHistory)}
          data-testid="toggle-history"
        >
          <History className="h-4 w-4 mr-2" />
          History
        </Button>
      </div>

      {/* Disclaimer */}
      <Card className="p-4 mb-6 border-warning/50 bg-warning/5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-warning">Research Use Only</p>
            <p className="text-muted-foreground">
              AI recommendations are for research purposes only. Not medical advice. 
              Always consult healthcare professionals before making any changes.
            </p>
          </div>
        </div>
      </Card>

      {/* Main Form */}
      <Card className="p-6 glass-card mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <Label className="text-lg mb-3 block">1. Choose a Focus Area</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {FOCUS_AREAS.map(area => {
                const Icon = area.icon;
                const selected = focusArea === area.value;
                return (
                  <button
                    key={area.value}
                    type="button"
                    onClick={() => setFocusArea(area.value)}
                    aria-pressed={selected}
                    data-testid={`focus-area-${area.value}`}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      selected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/40'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <Icon className={`h-5 w-5 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                      {selected && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/20 text-primary">
                          Selected
                        </span>
                      )}
                    </div>
                    <p className="font-medium">{area.label}</p>
                    <p className="text-xs text-muted-foreground">{area.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-6">
            <Label>2. Specific Question (Optional)</Label>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., How can I improve my BPC-157 protocol for better recovery? Should I adjust my timing?"
              rows={3}
              className="mt-2"
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading} data-testid="analyze-button">
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                Analyzing your data...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Get AI Recommendations
              </>
            )}
          </Button>
        </form>
      </Card>

      {/* Response */}
      {response && (
        <Card className="p-6 glass-card mb-6" data-testid="ai-response">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">AI Recommendations</h2>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {response.recommendations}
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Data analyzed: {response.data_analyzed?.dose_logs || 0} dose logs, {' '}
              {response.data_analyzed?.schedules || 0} schedules, {' '}
              {response.data_analyzed?.peptides_tracked || 0} peptides tracked
            </p>
            <p className="text-xs text-warning mt-2">{response.disclaimer}</p>
          </div>
        </Card>
      )}

      {/* History */}
      {showHistory && history.length > 0 && (
        <Card className="p-6 glass-card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <History className="h-5 w-5" />
            Previous Analyses
          </h2>
          <div className="space-y-3">
            {history.map((item, index) => (
              <div key={item.optimization_id || index} className="p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium capitalize">{item.focus_area} Analysis</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
                {item.question && (
                  <p className="text-sm text-muted-foreground">{item.question}</p>
                )}
                {item.response_preview && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {item.response_preview}...
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
